// src/components/channels/ConfigureInstagram.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Eye, EyeOff, CheckCircle, AlertTriangle, 
  ExternalLink, Copy, Info, Lock, Instagram
} from 'lucide-react';
import { channelService } from '../../services/PlatformService';

const ConfigureInstagram = ({ channel, onUpdate }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [formData, setFormData] = useState({
    instagramAccountId: '',
    accessToken: ''
  });

  useEffect(() => {
    // Se já tem configuração, preencher form
    if (channel.config) {
      setFormData(prev => ({ ...prev, ...channel.config }));
      if (channel.status === 'active') {
        setCurrentStep(3); // Já configurado
      } else if (channel.webhook_verified) {
        setCurrentStep(2); // Webhook configurado mas não ativo
      }
    }
  }, [channel]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const testConnection = async () => {
    if (!formData.instagramAccountId || !formData.accessToken) {
      alert('Por favor, preencha todos os campos antes de testar a conexão.');
      return;
    }

    setTesting(true);
    try {
      // Testar se o token é válido fazendo uma chamada simples ao Graph API
      const response = await fetch(
        `https://graph.instagram.com/v24.0/${formData.instagramAccountId}?fields=id,username&access_token=${formData.accessToken}`
      );
      
      const data = await response.json();
      
      if (response.ok && data.id) {
        alert(`✅ Conexão bem-sucedida!\n\nConta: @${data.username}\nID: ${data.id}`);
      } else {
        alert(`❌ Erro na conexão:\n\n${data.error?.message || 'Token ou ID inválido'}`);
      }
    } catch (error) {
      alert(`❌ Erro ao testar conexão:\n\n${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!formData.instagramAccountId || !formData.accessToken) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setSaving(true);
    try {
      await channelService.updateChannel(channel.id, {
        config: formData
      });
      setCurrentStep(2);
      onUpdate(); // Refresh channel data
    } catch (error) {
      console.error('Error saving credentials:', error);
      alert('Erro ao salvar credenciais');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyWebhook = async () => {
    const response = await channelService.getChannel(channel.id);
    const channelData = response.data;
    if (channelData.webhook_verified) {
      setCurrentStep(3);
      onUpdate();
    } else {
      alert('Configure o webhook no Meta App Dashboard. A verificação ainda não foi validada.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/channels')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <Instagram className="w-8 h-8 text-purple-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              Configurar {channel?.name}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Instagram className="w-6 h-6 text-purple-600" />
              Etapa 1: Configure suas Credenciais
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">Como obter suas credenciais:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Acesse o <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Meta App Dashboard</a></li>
                    <li>Crie um App (se não tiver) e adicione o produto "Instagram"</li>
                    <li>Vá em <strong>Instagram → Configurações Básicas</strong></li>
                    <li>Gere um <strong>Access Token</strong> com as permissões necessárias</li>
                    <li>Copie o <strong>Instagram Account ID (IG_ID)</strong></li>
                  </ol>
                  <p className="mt-2 font-medium">
                    📚 <a href="https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/get-started" target="_blank" rel="noopener noreferrer" className="underline">
                      Ver tutorial completo
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Permissões Necessárias */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium mb-2">Permissões necessárias no Access Token:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><code className="bg-purple-100 px-1 rounded">instagram_basic</code></li>
                    <li><code className="bg-purple-100 px-1 rounded">instagram_manage_messages</code></li>
                    <li><code className="bg-purple-100 px-1 rounded">pages_manage_metadata</code></li>
                    <li><code className="bg-purple-100 px-1 rounded">pages_read_engagement</code></li>
                    <li><code className="bg-purple-100 px-1 rounded">pages_show_list</code></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram Account ID (IG_ID) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.instagramAccountId}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagramAccountId: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="17841405726653026"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID numérico da sua conta Instagram Business/Creator
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showToken ? "text" : "password"}
                    value={formData.accessToken}
                    onChange={(e) => setFormData(prev => ({ ...prev, accessToken: e.target.value }))}
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                    placeholder="EAAFB..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(!showToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Instagram User Access Token com as permissões listadas acima
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={testConnection}
                disabled={testing || !formData.instagramAccountId || !formData.accessToken}
                className="px-6 py-3 bg-purple-100 text-purple-700 rounded-xl font-medium hover:bg-purple-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-all"
              >
                {testing ? 'Testando...' : '🔍 Testar Conexão'}
              </button>

              <button
                onClick={handleSaveCredentials}
                disabled={saving || !formData.instagramAccountId || !formData.accessToken}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
              >
                {saving ? 'Salvando...' : 'Salvar e Continuar'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Instagram className="w-6 h-6 text-purple-600" />
              Etapa 2: Configure o Webhook
            </h2>

            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">Atenção: Siga os passos exatamente como descrito</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">1. URL do Webhook:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={channel?.webhook_url || ''}
                      readOnly
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(channel?.webhook_url || '')}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                    >
                      {copied ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">2. Token de Verificação:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value="QUORAAI_VERIFY_TOKEN"
                      readOnly
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl font-mono text-sm font-bold"
                    />
                    <button
                      onClick={() => copyToClipboard('QUORAAI_VERIFY_TOKEN')}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">3. No Meta App Dashboard:</p>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Acesse seu App → <strong>Webhooks</strong></li>
                    <li>Cole a <strong>URL do Webhook</strong> no campo "Callback URL"</li>
                    <li>Cole o <strong>Token de Verificação</strong> no campo "Verify Token"</li>
                    <li>Clique em <strong>"Verify and Save"</strong></li>
                    <li>Inscreva-se no campo <strong>"messages"</strong> (Subscribe)</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-purple-900 mb-2">4. Subscribe aos Webhook Fields:</p>
                  <p className="text-sm text-purple-800 mb-2">Execute este comando (ou use o Dashboard):</p>
                  <div className="bg-purple-100 rounded p-3 font-mono text-xs overflow-x-auto">
                    curl -X POST "https://graph.instagram.com/v24.0/{formData.instagramAccountId}/subscribed_apps?subscribed_fields=messages&access_token={formData.accessToken}"
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <a 
                  href="https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/webhooks" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
                >
                  Ver tutorial completo <ExternalLink className="w-4 h-4" />
                </a>

                <button
                  onClick={handleVerifyWebhook}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-all flex items-center gap-2"
                >
                  <Lock className="w-5 h-5" />
                  Configurei o Webhook
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-purple-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Canal Instagram Configurado! 🎉
            </h2>
            
            <p className="text-gray-600 mb-8">
              Seu funcionário está pronto para atender pelo Instagram Direct
            </p>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/channels')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
              >
                Voltar aos Canais
              </button>
              
              <button
                onClick={() => navigate(`/channels/${channel.id}`)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Ver Detalhes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigureInstagram;